export interface UseCharacterCountOptions {
  /** Current field value ("" when unset — callers normalise non-string values). */
  value: string;
  /** Hard limit; drives the `used/max` text and the near-limit warning. */
  maxLength?: number;
  /** Soft length recommendation. Past it, the count becomes visible (progressive disclosure). */
  recommendedLength?: number;
  /** The component's `showCharacterCount` flag. */
  isEnabled?: boolean;
}

export interface CharacterCount {
  /** Value length in Unicode code points. */
  length: number;
  /** Value is past the soft `recommendedLength`. */
  overRecommended: boolean;
  /** Value is at ≥90% of the hard `maxLength`. */
  nearLimit: boolean;
  /** Whether the count should render: on whenever a length signal exists, but a
   *  `recommendedLength`-driven count stays hidden until the value is long enough to start
   *  getting shortened on cards (progressive disclosure); a `maxLength`-driven count (tagline)
   *  is always visible. */
  isVisible: boolean;
  /** `used/max` with a hard limit, bare `used` with only a recommendation. */
  text: string;
}

/**
 * Live character-count state shared by the wizard field family. Counts by Unicode code point
 * (spread), not UTF-16 code unit, so the client count matches Ruby's char-based `String#length`
 * on the server — emoji/astral titles agree on the cap.
 */
export function useCharacterCount({
  value,
  maxLength,
  recommendedLength,
  isEnabled,
}: UseCharacterCountOptions): CharacterCount {
  const hasMax = maxLength != null;
  const hasRecommended = recommendedLength != null;
  const length = [...value].length;
  const overRecommended = recommendedLength != null && length > recommendedLength;
  const nearLimit = hasMax && length >= (maxLength ?? 0) * 0.9;
  const isCounting = Boolean(isEnabled) && (hasMax || hasRecommended);
  const isVisible = isCounting && (hasRecommended ? overRecommended : true);
  const text = hasMax ? `${length}/${maxLength}` : `${length}`;
  return { length, overRecommended, nearLimit, isVisible, text };
}
